/**
 * Write a value into an uncontrolled input/textarea the way a user typing would.
 *
 * Assigning `.value` directly is not enough: React caches the last value it saw on the
 * node, sees no change, and skips the synthetic event - so grapheme counters and
 * setCustomValidity() handlers never run. Going through the prototype's value setter
 * updates React's tracker too, so the dispatched `input` event is delivered normally.
 */
function setNativeValue(
  el: HTMLInputElement | HTMLTextAreaElement,
  value: string,
) {
  const prototype =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  // Pulling a native `value` setter off the prototype to bypass React's own
  // descriptor. It is invoked as `setter.call(el, value)` below, so the
  // receiver is supplied explicitly and unbound-method's concern does not
  // apply — and annotating `this: void` would be untrue, since a native
  // setter does use its receiver.
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const setter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;

  if (setter) {
    setter.call(el, value);
  } else {
    el.value = value;
  }

  el.dispatchEvent(new Event("input", { bubbles: true }));
}

/**
 * Fill a named form field, but never clobber something the user already wrote.
 *
 * `maxLength` is enforced here because the attribute only constrains typing - a
 * programmatic write can exceed it and would fail validation on submit instead.
 * Returns true if the field was filled.
 */
export function fillIfEmpty(
  form: HTMLFormElement | null | undefined,
  name: string,
  value: string | undefined,
  maxLength?: number,
): boolean {
  if (!form || !value) return false;

  const el = form.elements.namedItem(name);
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) {
    return false;
  }
  if (el.value.trim()) return false;

  const trimmed = value.trim();
  setNativeValue(el, maxLength ? trimmed.slice(0, maxLength) : trimmed);
  return true;
}
